import {
  Button,
  Input,
  Select,
  SelectItem,
  Textarea,
} from '@nextui-org/react-latest'
import createSupportRequest from '@services/api/support/createSupportRequest'
import styled from '@emotion/styled'
import { colors, fontFamilies } from '@configs/styles/config'
import { useState } from 'react'
import dynamic from 'next/dynamic'
import contactAnimation from '@public/static/images/animations/contactUsAnimation.json'
import { useTranslation } from 'react-i18next'

const Lottie = dynamic(() => import('react-lottie'), {
  ssr: false,
  loading: () => <div style={{ minHeight: '400px', width: '100%' }} />,
})

const PageWrapper = styled.div`
  max-width: 1380px;
  margin: 0 auto;
  padding: 3rem 1rem 5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`

const LeftCol = styled.div``

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;

  @media (max-width: 900px) {
    display: none;
  }
`

const IllustrationCaption = styled.div`
  text-align: center;
`

const IllustrationTitle = styled.h2`
  font-size: 28px;
  font-family: ${fontFamilies.bold};
  color: ${colors.uguPurple};
  margin-bottom: 0.5rem;
`

const IllustrationSubtitle = styled.p`
  font-size: 16px;
  font-family: ${fontFamilies.regular};
  color: ${colors.uguDarkGrey};
  max-width: 380px;
  margin: 0 auto;
  line-height: 1.6;
`

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${colors.uguLightBrightPurple};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
`

const InfoText = styled.div``

const InfoLabel = styled.div`
  font-size: 13px;
  font-family: ${fontFamilies.medium};
  color: ${colors.uguDarkGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const InfoValue = styled.div`
  font-size: 15px;
  font-family: ${fontFamilies.medium};
  color: ${colors.uguPurple};
`

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 380px;
`

const PageTitle = styled.h1`
  font-size: 42px;
  font-family: ${fontFamilies.bold};
  color: ${colors.uguPurple};
  margin-bottom: 0.5rem;
  @media (max-width: 640px) {
    font-size: 30px;
  }
`

const PageSubtitle = styled.p`
  font-size: 18px;
  font-family: ${fontFamilies.regular};
  color: ${colors.uguDarkGrey};
  margin-bottom: 2rem;
  max-width: 560px;
`

const FormCard = styled.div`
  background: #fff;
  border: 1px solid ${colors.uguGray};
  border-radius: 1.5rem;
  padding: 2rem;
  @media (max-width: 640px) {
    padding: 1.5rem 1rem;
  }
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const FullWidth = styled.div`
  grid-column: 1 / -1;
`

const SuccessBox = styled.div`
  background: ${colors.uguIvory};
  border: 1px solid ${colors.uguBrightPurple};
  border-radius: 1rem;
  padding: 2rem;
  color: ${colors.uguPurple};
  font-family: ${fontFamilies.medium};
  font-size: 16px;
  text-align: center;
`

type FormState = {
  fullName: string
  email: string
  phone: string
  message: string
  type: string
  amountOfCourses: string
  studentAudience: string
}

const initialForm: FormState = {
  fullName: '',
  email: '',
  phone: '',
  message: '',
  type: 'generalInquiry',
  amountOfCourses: '0to1',
  studentAudience: 'lessThen5000',
}

const lottieOptions = {
  loop: true,
  autoplay: true,
  animationData: contactAnimation,
  rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
}

const ContactUsPage = () => {
  const { t } = useTranslation(['contactUs'])

  const requestTypes = [
    { value: 'generalInquiry', label: t('requestTypes.generalInquiry') },
    {
      value: 'introductionToEdugramFeatures',
      label: t('requestTypes.introductionToEdugramFeatures'),
    },
    {
      value: 'accountSetupAndVerification',
      label: t('requestTypes.accountSetupAndVerification'),
    },
    {
      value: 'contentCreationAndPublishingGuidelines',
      label: t('requestTypes.contentCreationAndPublishingGuidelines'),
    },
    {
      value: 'earningOpportunitiesAndRevenueModels',
      label: t('requestTypes.earningOpportunitiesAndRevenueModels'),
    },
    {
      value: 'technicalSupportAndTroubleshooting',
      label: t('requestTypes.technicalSupportAndTroubleshooting'),
    },
    {
      value: 'feedbackAndImprovementSuggestions',
      label: t('requestTypes.feedbackAndImprovementSuggestions'),
    },
    { value: 'other', label: t('requestTypes.other') },
  ]

  const amountOfCoursesOptions = [
    { value: '0to1', label: t('amountOfCoursesOptions.0to1') },
    { value: '1to5', label: t('amountOfCoursesOptions.1to5') },
    { value: '6plus', label: t('amountOfCoursesOptions.6plus') },
  ]

  const studentAudienceOptions = [
    {
      value: 'lessThen5000',
      label: t('studentAudienceOptions.lessThen5000'),
    },
    {
      value: 'moreThen5000',
      label: t('studentAudienceOptions.moreThen5000'),
    },
    {
      value: 'moreThen20000',
      label: t('studentAudienceOptions.moreThen20000'),
    },
  ]

  const [form, setForm] = useState<FormState>(initialForm)
  const [errors, setErrors] = useState<Partial<FormState>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [serverError, setServerError] = useState('')

  const validate = (): boolean => {
    const newErrors: Partial<FormState> = {}
    if (!form.fullName.trim()) newErrors.fullName = t('errors.fullNameRequired')
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = t('errors.validEmailRequired')
    if (!form.phone.trim()) newErrors.phone = t('errors.phoneRequired')
    if (form.message.trim().length < 50)
      newErrors.message = t('errors.messageMinLength')
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setIsLoading(true)
    setServerError('')
    try {
      await createSupportRequest({ ...form, action: 'Contact Us' })
      setIsSuccess(true)
      setForm(initialForm)
    } catch {
      setServerError(t('errors.serverError'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PageWrapper>
      {/* Left: Form */}
      <LeftCol>
        <PageTitle>{t('title')}</PageTitle>
        <PageSubtitle>{t('subtitle')}</PageSubtitle>

        <FormCard>
          {isSuccess ? (
            <SuccessBox>{t('successMessage')}</SuccessBox>
          ) : (
            <form onSubmit={handleSubmit}>
              <FormGrid>
                <div>
                  <Input
                    label={t('form.fullName.label')}
                    placeholder={t('form.fullName.placeholder')}
                    value={form.fullName}
                    onValueChange={(v) => handleChange('fullName', v)}
                    isInvalid={!!errors.fullName}
                    errorMessage={errors.fullName}
                    radius="lg"
                    classNames={{ label: 'z-0' }}
                  />
                </div>
                <div>
                  <Input
                    label={t('form.email.label')}
                    placeholder={t('form.email.placeholder')}
                    type="email"
                    value={form.email}
                    onValueChange={(v) => handleChange('email', v)}
                    isInvalid={!!errors.email}
                    errorMessage={errors.email}
                    radius="lg"
                    classNames={{ label: 'z-0' }}
                  />
                </div>
                <div>
                  <Input
                    label={t('form.phone.label')}
                    placeholder={t('form.phone.placeholder')}
                    value={form.phone}
                    onValueChange={(v) => handleChange('phone', v)}
                    isInvalid={!!errors.phone}
                    errorMessage={errors.phone}
                    radius="lg"
                    classNames={{ label: 'z-0' }}
                  />
                </div>
                <div>
                  <Select
                    label={t('form.topic.label')}
                    selectedKeys={[form.type]}
                    onSelectionChange={(keys) =>
                      handleChange('type', Array.from(keys)[0] as string)
                    }
                    radius="lg"
                    classNames={{ label: 'z-0' }}
                  >
                    {requestTypes.map((t) => (
                      <SelectItem color="primary" key={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div>
                  <Select
                    label={t('form.amountOfCourses.label')}
                    selectedKeys={[form.amountOfCourses]}
                    onSelectionChange={(keys) =>
                      handleChange(
                        'amountOfCourses',
                        Array.from(keys)[0] as string
                      )
                    }
                    radius="lg"
                    classNames={{ label: 'z-0' }}
                  >
                    {amountOfCoursesOptions.map((o) => (
                      <SelectItem color="primary" key={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div>
                  <Select
                    label={t('form.studentAudience.label')}
                    selectedKeys={[form.studentAudience]}
                    onSelectionChange={(keys) =>
                      handleChange(
                        'studentAudience',
                        Array.from(keys)[0] as string
                      )
                    }
                    radius="lg"
                    classNames={{ label: 'z-0' }}
                  >
                    {studentAudienceOptions.map((o) => (
                      <SelectItem color="primary" key={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <FullWidth>
                  <Textarea
                    label={t('form.message.label')}
                    placeholder={t('form.message.placeholder')}
                    value={form.message}
                    onValueChange={(v) => handleChange('message', v)}
                    isInvalid={!!errors.message}
                    errorMessage={errors.message}
                    minRows={5}
                    radius="lg"
                    classNames={{ label: 'z-0' }}
                  />
                </FullWidth>
                {serverError && (
                  <FullWidth>
                    <p className="text-sm text-red-500">{serverError}</p>
                  </FullWidth>
                )}
                <FullWidth>
                  <Button
                    type="submit"
                    radius="full"
                    size="lg"
                    className="font-medium w-full sm:w-auto px-10"
                    isLoading={isLoading}
                    color="primary"
                  >
                    {t('form.submit')}
                  </Button>
                </FullWidth>
              </FormGrid>
            </form>
          )}
        </FormCard>
      </LeftCol>

      {/* Right: Illustration */}
      <RightCol>
        <div style={{ width: '100%', maxWidth: 440 }}>
          <Lottie options={lottieOptions} />
        </div>
        <IllustrationCaption>
          <IllustrationTitle>{t('illustration.title')}</IllustrationTitle>
          <IllustrationSubtitle>
            {t('illustration.subtitle')}
          </IllustrationSubtitle>
        </IllustrationCaption>
        <InfoList>
          <InfoItem>
            <InfoIcon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z"
                  stroke="#7181FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 6L12 13L2 6"
                  stroke="#7181FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </InfoIcon>
            <InfoText>
              <InfoLabel>{t('info.email.label')}</InfoLabel>
              <InfoValue>support@edugram.io</InfoValue>
            </InfoText>
          </InfoItem>
          <InfoItem>
            <InfoIcon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#7181FF"
                  strokeWidth="2"
                />
                <path
                  d="M12 6V12L16 14"
                  stroke="#7181FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </InfoIcon>
            <InfoText>
              <InfoLabel>{t('info.responseTime.label')}</InfoLabel>
              <InfoValue>{t('info.responseTime.value')}</InfoValue>
            </InfoText>
          </InfoItem>
          <InfoItem>
            <InfoIcon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#7181FF"
                  strokeWidth="2"
                />
                <path
                  d="M2 12H22"
                  stroke="#7181FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 2C9.33 6.67 9.33 17.33 12 22"
                  stroke="#7181FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 2C14.67 6.67 14.67 17.33 12 22"
                  stroke="#7181FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </InfoIcon>
            <InfoText>
              <InfoLabel>{t('info.supportHours.label')}</InfoLabel>
              <InfoValue>{t('info.supportHours.value')}</InfoValue>
            </InfoText>
          </InfoItem>
        </InfoList>
      </RightCol>
    </PageWrapper>
  )
}

export default ContactUsPage
